/**
 * The rules of Excalibur, as one pure state machine.
 *
 * Three front ends run on this exact file — the authoritative server, the
 * pass-and-play mode, and the bot game — so the rules cannot diverge between
 * them. That is the whole reason it is here in `shared/` and not in `server/`.
 *
 * ── Why a step machine and not closures ──────────────────────────────────────
 * Almost every ability is interactive and multi-step ("draw 2, THEN return 1",
 * "peek, THEN decide whether to swap"). The obvious implementation is a
 * generator or a chain of callbacks. Both are unserialisable, and this state has
 * to survive `JSON.stringify` twice: once for the snapshot the server writes on
 * SIGTERM, and once for every view pushed to a socket. So an ability in progress
 * is plain data — `{ chip, actor, step, scratch }` on a stack — and `runAbility`
 * is a switch on `step`. Verbose, but it round-trips through JSON and it can be
 * dumped into a bug report.
 *
 * ── The invariant that matters ───────────────────────────────────────────────
 * The engine never reads a random number except through `rand()`, which advances
 * a seed held ON the state. Same seed plus same actions produce the same game,
 * which is what makes the test suite able to assert on a shuffle at all.
 */

import { CHIPS, isSword, isTrashable, fullBox, type ChipId } from './chips.js';

// ── shape ───────────────────────────────────────────────────────────────────

export type Phase =
  /** Waiting for players. */
  | 'lobby'
  /** Everyone simultaneously draws back up to four. */
  | 'draw'
  /** Everyone simultaneously conceals one chip in a fist. */
  | 'play'
  /** Fists open. On Reveal and Permanent effects fire, in turn order. */
  | 'reveal'
  /** Crown holder clockwise, each played chip resolves. */
  | 'resolve'
  /** A winner has been determined. */
  | 'over';

export type Direction = 'left' | 'right';

export interface Player {
  id: string;
  name: string;
  /** Secret. Redacted out of every view but this player's own. */
  hand: ChipId[];
  /**
   * The chip concealed in this player's fist. It has already LEFT the hand —
   * which is why Lady of the Lake and Mordred, who move whole hands, never move
   * a played chip.
   */
  played: ChipId | null;
  /** Set by Lancelot: the flip failed, so the chip is trashed and never resolves. */
  playedKilled: boolean;
  /** Faceup chips in play by this player, granting their ability. Fisher King. */
  permanents: ChipId[];
  /**
   * Galahad. One copy of this chip id in the hand is untouchable this round.
   * Stored as an id rather than an index because the hand is reordered by every
   * steal; `takeOne` removes a single occurrence, which is the right semantics
   * when you hold two Merchants and protected one of them.
   */
  protectedChip: ChipId | null;
  /** Fisher King is faceup: "steal" takes 0 chips from this player this round. */
  fisherKingUp: boolean;
  /** Fisher King owners are offered their flip once per round. */
  fisherKingAsked: boolean;
  isBot: boolean;
}

/** A decision the game is blocked on. Exactly one player can answer it. */
export type Pending =
  /** Pick one of Avalon's three regions to draw from, or to place into. */
  | { kind: 'region'; playerId: string; chip: ChipId; prompt: string; allowEmpty: boolean }
  /** A branch in the ability text, e.g. "Draw 2 chips OR steal 2 chips". */
  | { kind: 'mode'; playerId: string; chip: ChipId; prompt: string; modes: { id: string; label: string }[] }
  /** Point at players. */
  | { kind: 'players'; playerId: string; chip: ChipId; prompt: string; min: number; max: number; candidates: string[] }
  /** Choose chips out of your own hand. */
  | { kind: 'hand'; playerId: string; chip: ChipId; prompt: string; min: number; max: number; eligible: ChipId[] }
  /** Left or right. */
  | { kind: 'direction'; playerId: string; chip: ChipId; prompt: string }
  /** Choose from a specific faceup set — Percival's four, or Morgana's two. */
  | { kind: 'pick'; playerId: string; chip: ChipId; prompt: string; chips: ChipId[]; optional: boolean }
  /** Yes or no. */
  | { kind: 'confirm'; playerId: string; chip: ChipId; prompt: string; yes: string; no: string }
  /**
   * Choose a chip out of a hand you are allowed to see. `peek` is secret and is
   * redacted from every other player's view.
   */
  | { kind: 'peek'; playerId: string; chip: ChipId; prompt: string; targetId: string; peek: ChipId[]; mode: 'swap' | 'expose' }
  /** Gawain: the victim chooses which of their swords to hand over. */
  | { kind: 'sword'; playerId: string; chip: ChipId; prompt: string; swords: ChipId[] }
  /** Take one of the played chips on the table. Black Knight. */
  | { kind: 'played'; playerId: string; chip: ChipId; prompt: string; options: { ownerId: string; chip: ChipId }[] };

export type Answer =
  | { kind: 'region'; region: number }
  | { kind: 'mode'; mode: string }
  | { kind: 'players'; players: string[] }
  | { kind: 'hand'; chips: ChipId[] }
  | { kind: 'direction'; direction: Direction }
  | { kind: 'pick'; index: number | null }
  | { kind: 'confirm'; yes: boolean }
  | { kind: 'peek'; index: number | null; swap?: boolean }
  | { kind: 'sword'; chip: ChipId }
  | { kind: 'played'; ownerId: string; chip: ChipId };

/** An ability part-way through resolving. Plain data, on a stack. */
export interface Resolution {
  chip: ChipId;
  actor: string;
  step: number;
  scratch: Scratch;
  /** Spawned by Morgana. Nested resolutions do not re-trigger the end check. */
  nested: boolean;
}

interface Scratch {
  mode?: string;
  targets?: string[];
  drawn?: ChipId[];
  taken?: ChipId[];
  /** How many draws or placements are still owed. */
  left?: number;
  direction?: Direction;
  revealed?: ChipId[];
  /** Merlin: who surrendered a sword and therefore gets to steal one back. */
  givers?: string[];
  cursor?: number;
  place?: ChipId[];
  victimId?: string;
  /** Where a "return" has to go back to: Avalon, or the player it came from. */
  returnTo?: 'avalon' | 'victim';
}

/** Public narration. Everything here is safe for every player to see. */
export interface LogEntry {
  round: number;
  text: string;
  /** Rendered with emphasis — reveals, huzzahs, sword movements. */
  loud?: boolean;
}

export interface GameState {
  phase: Phase;
  players: Player[];
  /** Seat index of the crown holder. Turn order starts here and goes clockwise. */
  crown: number;
  /** Avalon, split into three regions by the blade replicas. Facedown. */
  regions: [ChipId[], ChipId[], ChipId[]];
  /** Trashed faceup to the box. Public, and memorising it is a real skill. */
  trash: ChipId[];
  round: number;
  /**
   * Latched by the region-empty check. "Even if chips are later added back into
   * Avalon" — so this never goes back to false.
   */
  finalRound: boolean;
  /** Turn order for this round: crown holder first, then clockwise. */
  order: string[];
  /** Reveal-phase queue: On Reveal and Permanent chips, in turn order. */
  revealQueue: string[];
  /** How long that queue was when the step began. The queue itself is consumed,
   *  so without this there is no way to say "second of four". */
  revealTotal: number;
  /** Fisher King owners still owed their "flip it?" offer this round. */
  flipQueue: string[];
  /** Resolve-phase queue: ordinary chips, in turn order. */
  resolveQueue: string[];
  resolveTotal: number;
  stack: Resolution[];
  pending: Pending | null;
  /** Monotonic. Answers name it, so a duplicate tap cannot answer twice. */
  pendingId: number;
  log: LogEntry[];
  /** mulberry32 state. Never leaves the server. */
  rng: number;
  outcome: Outcome | null;
}

export interface Outcome {
  /** Everyone who wins. Empty means the kingdom fell into chaos. */
  winners: string[];
  /** Whoever held the Cursed Blade. */
  ultimateLoser: string | null;
  /** Excalibur and the Cursed Blade in one hand: everyone ELSE shares a lesser win. */
  tainted: boolean;
  excaliburHolder: string | null;
  squireHolder: string | null;
  headline: string;
}

// ── randomness ──────────────────────────────────────────────────────────────

/** mulberry32. Small, fast, and seedable — the test suite depends on the last part. */
function rand(s: GameState): number {
  s.rng = (s.rng + 0x6d2b79f5) >>> 0;
  let t = s.rng;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function randInt(s: GameState, n: number): number {
  return Math.floor(rand(s) * n) % Math.max(1, n);
}

export function seedFrom(text: string): number {
  let a = 0x9e3779b9;
  for (let i = 0; i < text.length; i++) a = (Math.imul(a ^ text.charCodeAt(i), 0x85ebca6b) + i) >>> 0;
  return a >>> 0;
}

function shuffleInPlace<T>(s: GameState, arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = randInt(s, i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

/** Remove ONE occurrence of `chip`. Returns whether it was there. */
function takeOne(list: ChipId[], chip: ChipId): boolean {
  const i = list.indexOf(chip);
  if (i < 0) return false;
  list.splice(i, 1);
  return true;
}

// ── lookups ─────────────────────────────────────────────────────────────────

export function playerOf(s: GameState, id: string): Player | undefined {
  return s.players.find((p) => p.id === id);
}

function seatOf(s: GameState, id: string): number {
  return s.players.findIndex((p) => p.id === id);
}

/** Clockwise is to the left, which is the next seat. */
function neighbour(s: GameState, id: string, dir: Direction): Player {
  const i = seatOf(s, id);
  const n = s.players.length;
  const j = dir === 'left' ? (i + 1) % n : (i - 1 + n) % n;
  return s.players[j]!;
}

/** Turn order from the crown, clockwise — the tiebreak for literally everything. */
function turnOrder(s: GameState): string[] {
  const n = s.players.length;
  const out: string[] = [];
  for (let k = 0; k < n; k++) out.push(s.players[(s.crown + k) % n]!.id);
  return out;
}

export function avalonCount(s: GameState): number {
  return s.regions[0].length + s.regions[1].length + s.regions[2].length;
}

/** Chips of a hand that an effect is allowed to touch. Galahad shields one. */
function reachable(p: Player): ChipId[] {
  const out = p.hand.slice();
  if (p.protectedChip) takeOne(out, p.protectedChip);
  return out;
}

function swordsOf(p: Player): ChipId[] {
  return reachable(p).filter(isSword);
}

function log(s: GameState, text: string, loud = false) {
  s.log.push({ round: s.round, text, loud });
  // The log is narration, not an audit trail. Six players over twenty rounds
  // generate a few hundred lines, and every one of them ships in every view.
  if (s.log.length > 120) s.log.splice(0, s.log.length - 120);
}

function nameOf(s: GameState, id: string): string {
  return playerOf(s, id)?.name ?? 'someone';
}

// ── primitive moves ─────────────────────────────────────────────────────────

/**
 * Take the top facedown chip of a region. Which physical chip you get is random
 * because the region is facedown and mixed — the player chooses the region, not
 * the chip, and that is the whole of the decision.
 */
function drawFrom(s: GameState, p: Player, region: number): ChipId | null {
  const r = s.regions[region as 0 | 1 | 2];
  if (!r || r.length === 0) return null;
  const chip = r.splice(randInt(s, r.length), 1)[0]!;
  p.hand.push(chip);
  return chip;
}

function placeIn(s: GameState, chip: ChipId, region: number) {
  const r = s.regions[region as 0 | 1 | 2] ?? s.regions[0];
  r.splice(randInt(s, r.length + 1), 0, chip);
}

/**
 * Steal up to `n` random chips. Returns what actually moved.
 *
 * Fisher King, faceup, makes this take zero — which is why the check is here in
 * the primitive rather than in each of the six abilities that steal.
 */
function steal(s: GameState, thief: Player, victim: Player, n: number): ChipId[] {
  if (victim.id === thief.id) return [];
  if (victim.fisherKingUp) {
    log(s, `The Fisher King shields ${victim.name}. Nothing is taken.`);
    return [];
  }
  const pool = reachable(victim);
  const got: ChipId[] = [];
  for (let i = 0; i < n && pool.length > 0; i++) {
    const chip = pool.splice(randInt(s, pool.length), 1)[0]!;
    takeOne(victim.hand, chip);
    thief.hand.push(chip);
    got.push(chip);
  }
  return got;
}

function trash(s: GameState, chip: ChipId) {
  s.trash.push(chip);
}

/**
 * Collect every sword on the table, honouring Galahad. Used by the five
 * abilities that move swords wholesale.
 */
function harvestSwords(s: GameState, opts: { includeIds?: string[]; exclude?: string } = {}) {
  const taken: { ownerId: string; chip: ChipId }[] = [];
  for (const p of s.players) {
    if (opts.exclude && p.id === opts.exclude) continue;
    if (opts.includeIds && !opts.includeIds.includes(p.id)) continue;
    for (const sw of swordsOf(p)) {
      takeOne(p.hand, sw);
      taken.push({ ownerId: p.id, chip: sw });
    }
  }
  return taken;
}

/** A coin toss. Faceup is a win. */
function flip(s: GameState): boolean {
  return rand(s) < 0.5;
}

// ── setup ───────────────────────────────────────────────────────────────────

export interface SetupPlayer {
  id: string;
  name: string;
  isBot?: boolean;
}

/**
 * Rulebook setup, in its own order.
 *
 * The one part worth spelling out is ④: the crown holder takes Excalibur and the
 * Cursed Blade, tops up from Avalon to exactly one chip per player, mixes those
 * facedown and deals one to everybody. So at 2 players the two key swords ARE
 * the deal, and at 8 the crown holder adds six ordinary chips to hide them in.
 * The Squire Sword is not part of this — it stays anonymous in Avalon.
 */
export function newGame(setup: SetupPlayer[], seed: number): GameState {
  const s: GameState = {
    phase: 'draw',
    players: setup.map((p) => ({
      id: p.id,
      name: p.name,
      hand: [],
      played: null,
      playedKilled: false,
      permanents: [],
      protectedChip: null,
      fisherKingUp: false,
      fisherKingAsked: false,
      isBot: !!p.isBot,
    })),
    crown: 0,
    regions: [[], [], []],
    trash: [],
    round: 1,
    finalRound: false,
    order: [],
    revealQueue: [],
    revealTotal: 0,
    flipQueue: [],
    resolveQueue: [],
    resolveTotal: 0,
    stack: [],
    pending: null,
    pendingId: 0,
    log: [],
    rng: seed >>> 0,
    outcome: null,
  };

  const box = fullBox();
  // ② the two key chips come out before anything is mixed.
  takeOne(box, 'excalibur');
  takeOne(box, 'cursedBlade');
  // ③ everything else becomes Avalon.
  shuffleInPlace(s, box);

  // ④ top up to one chip per player, mix, deal one each.
  const parcel: ChipId[] = ['excalibur', 'cursedBlade'];
  const topUp = Math.max(0, s.players.length - 2);
  for (let i = 0; i < topUp; i++) parcel.push(box.pop()!);
  shuffleInPlace(s, parcel);
  s.players.forEach((p, i) => p.hand.push(parcel[i]!));

  // ⑤ the blade replicas split what remains into three roughly equal regions.
  box.forEach((chip, i) => s.regions[(i % 3) as 0 | 1 | 2].push(chip));
  s.regions.forEach((r) => shuffleInPlace(s, r));

  s.order = turnOrder(s);
  // Phrased so it does not conjugate against the player's name. Names are
  // arbitrary strings and one of them is very often "You", which turns every
  // third-person verb in the log into "You takes the crown".
  log(s, `The crown goes to ${s.players[s.crown]!.name}. Avalon is dealt.`, true);
  beginDraw(s);
  return s;
}

// ── the round ───────────────────────────────────────────────────────────────

function beginDraw(s: GameState) {
  s.phase = 'draw';
  s.order = turnOrder(s);
  for (const p of s.players) {
    p.played = null;
    p.playedKilled = false;
    p.protectedChip = null;
    p.fisherKingUp = false;
    p.fisherKingAsked = false;
  }
  // A round where nobody can draw and nobody can play would spin forever.
  advanceIfDrawComplete(s);
}

/** How many chips this player still owes the draw step. */
export function drawsOwed(p: Player): number {
  return Math.max(0, 4 - p.hand.length);
}

function advanceIfDrawComplete(s: GameState) {
  if (s.phase !== 'draw') return;
  const stillOwed = s.players.some((p) => drawsOwed(p) > 0);
  // Avalon can run dry mid-draw. The rulebook's end trigger fires on an empty
  // REGION, so a dry Avalon always means the game is already ending — but the
  // round still has to finish, and it cannot finish if it waits for a draw that
  // can never happen.
  if (stillOwed && avalonCount(s) > 0) return;
  beginPlay(s);
}

function beginPlay(s: GameState) {
  s.phase = 'play';
  // Anyone holding nothing but swords is skipped rather than blocking the round.
  // With a full hand of four that is impossible — only three swords exist — but
  // it is reachable late, once Avalon has run dry and hands are short. The skip
  // itself lives in `advanceIfPlayComplete`, which waits only on players who
  // actually have something they could play.
  advanceIfPlayComplete(s);
}

function playableOf(p: Player): ChipId[] {
  return p.hand.filter((c) => !isSword(c));
}

function advanceIfPlayComplete(s: GameState) {
  if (s.phase !== 'play') return;
  const waiting = s.players.some((p) => p.played === null && playableOf(p).length > 0);
  if (waiting) return;
  beginReveal(s);
}

function beginReveal(s: GameState) {
  s.phase = 'reveal';
  const shown = s.order
    .map((id) => playerOf(s, id)!)
    .filter((p) => p.played)
    .map((p) => `${p.name} played ${CHIPS[p.played!].name}`);
  if (shown.length) log(s, `Fists open. ${shown.join('. ')}.`, true);

  // On Reveal and Permanent both resolve here, before ordinary resolution, and
  // among themselves in turn order.
  s.revealQueue = s.order.filter((id) => {
    const p = playerOf(s, id)!;
    if (!p.played) return false;
    const def = CHIPS[p.played];
    return !!def.onReveal || !!def.permanent;
  });
  s.revealTotal = s.revealQueue.length;
  pump(s);
}

function beginResolve(s: GameState) {
  s.phase = 'resolve';
  // "You may flip Fisher King anytime." Anytime is unimplementable as written in
  // an asynchronous game, so the offer lands at the one moment the decision is
  // informed and still matters: fists are open, every played chip is visible, and
  // no steal has happened yet.
  s.flipQueue = s.order.filter((id) => {
    const p = playerOf(s, id)!;
    return p.permanents.includes('fisherKing') && !p.fisherKingAsked;
  });
  s.resolveQueue = s.order.filter((id) => {
    const p = playerOf(s, id)!;
    if (!p.played || p.playedKilled) return false;
    const def = CHIPS[p.played];
    return !def.onReveal && !def.permanent;
  });
  s.resolveTotal = s.resolveQueue.length;
  pump(s);
}

function endRound(s: GameState) {
  // Anything still in a fist that was not a Permanent is trashed faceup.
  for (const p of s.players) {
    if (p.played) {
      const def = CHIPS[p.played];
      if (def.permanent && !p.playedKilled) {
        p.permanents.push(p.played);
        log(s, `${p.name} keeps the ${def.name} in play.`);
      } else {
        trash(s, p.played);
      }
      p.played = null;
    }
  }
  checkEndTrigger(s);
  if (s.finalRound) {
    finish(s);
    return;
  }
  // ⑤ Pass the crown to the left.
  s.crown = (s.crown + 1) % s.players.length;
  s.round += 1;
  log(s, `Round ${s.round}. The crown passes to ${s.players[s.crown]!.name}, who now goes first.`);
  beginDraw(s);
}

/**
 * "If any 1 region of Avalon is empty (or 2 regions for 6-8 players), then the
 * current round becomes the final game round."
 *
 * Latching, deliberately: the rulebook adds "even if chips are later added back
 * into Avalon", and Artisan puts chips back every round.
 */
function checkEndTrigger(s: GameState) {
  if (s.finalRound) return;
  const empty = s.regions.filter((r) => r.length === 0).length;
  const needed = s.players.length >= 6 ? 2 : 1;
  if (empty >= needed) {
    s.finalRound = true;
    log(s, empty >= 2 ? 'Two regions of Avalon lie empty. This is the final round.' : 'A region of Avalon is empty. This is the final round.', true);
  }
}

// ── ending ──────────────────────────────────────────────────────────────────

/**
 * Win evaluation, in the rulebook's own order of precedence.
 *
 * The published review coverage of this game gets the Excalibur + Cursed Blade
 * case backwards — it reads as "you are the ultimate loser". The rulebook is
 * explicit that every OTHER player shares a tainted win, so that is what runs
 * here.
 */
export function evaluate(s: GameState): Outcome {
  const holder = (chip: ChipId) => s.players.find((p) => p.hand.includes(chip))?.id ?? null;
  const excaliburHolder = holder('excalibur');
  const cursedHolder = holder('cursedBlade');
  const squireHolder = holder('squireSword');

  const base: Outcome = {
    winners: [],
    ultimateLoser: cursedHolder,
    tainted: false,
    excaliburHolder,
    squireHolder,
    headline: '',
  };

  if (!excaliburHolder) {
    return { ...base, headline: 'Excalibur is lost in Avalon. The kingdom falls to chaos — everybody loses.' };
  }

  if (excaliburHolder === cursedHolder) {
    // The sword and the curse in one hand. Everyone else takes the crown by
    // default, and it is worth less for it.
    const others = s.players.filter((p) => p.id !== excaliburHolder).map((p) => p.id);
    return {
      ...base,
      winners: others,
      tainted: true,
      headline: `${nameOf(s, excaliburHolder)} drew Excalibur and the Cursed Blade together. Everyone else shares a tainted win.`,
    };
  }

  const winners = [excaliburHolder];
  // The Squire Sword rides on the winner to its right — disabled at 2 and 3
  // players, where "the player on your left" is nearly everyone.
  if (squireHolder && s.players.length >= 4) {
    const toTheirLeft = neighbour(s, squireHolder, 'left');
    if (toTheirLeft.id === excaliburHolder && squireHolder !== excaliburHolder) winners.push(squireHolder);
  }

  let headline = `${nameOf(s, excaliburHolder)} holds Excalibur and takes the crown.`;
  if (winners.length > 1) headline += ` ${nameOf(s, winners[1]!)} rides to victory on the Squire Sword.`;
  if (cursedHolder) headline += ` ${nameOf(s, cursedHolder)} is left holding the Cursed Blade.`;
  return { ...base, winners, headline };
}

function finish(s: GameState) {
  s.phase = 'over';
  s.pending = null;
  s.stack = [];
  s.outcome = evaluate(s);
  log(s, s.outcome.headline, true);
}

// ── the pump ────────────────────────────────────────────────────────────────

/**
 * Drive the machine forward until it needs an answer or the round is done.
 *
 * The loop is bounded rather than `while (true)`: an ability that forgets to
 * advance its own step would otherwise hang the server process for every room,
 * not just this one. If the bound is ever hit that is a bug in an ability, and
 * failing loudly beats spinning.
 */
function pump(s: GameState) {
  for (let guard = 0; guard < 2000; guard++) {
    if (s.pending || s.phase === 'over') return;

    if (s.stack.length > 0) {
      runAbility(s, s.stack[s.stack.length - 1]!);
      continue;
    }

    if (s.phase === 'reveal') {
      const next = s.revealQueue.shift();
      if (!next) {
        beginResolve(s);
        return;
      }
      const p = playerOf(s, next)!;
      if (!p.played || p.playedKilled) continue;
      push(s, p.played, p.id);
      continue;
    }

    if (s.phase === 'resolve') {
      const flipper = s.flipQueue.shift();
      if (flipper) {
        const p = playerOf(s, flipper)!;
        if (!p.permanents.includes('fisherKing') || p.fisherKingAsked) continue;
        p.fisherKingAsked = true;
        ask(s, {
          kind: 'confirm',
          playerId: p.id,
          chip: 'fisherKing',
          prompt: 'Flip the Fisher King? On a win, nothing can be stolen from you this round. On a failure it is trashed.',
          yes: 'Flip it',
          no: 'Hold',
        });
        return;
      }
      const next = s.resolveQueue.shift();
      if (!next) {
        endRound(s);
        return;
      }
      const p = playerOf(s, next)!;
      if (!p.played || p.playedKilled) continue;
      push(s, p.played, p.id);
      continue;
    }

    return;
  }
  throw new Error('[excalibur] resolution did not converge — an ability failed to advance its step');
}

function push(s: GameState, chip: ChipId, actor: string, nested = false) {
  s.stack.push({ chip, actor, step: 0, scratch: {}, nested });
}

function done(s: GameState) {
  s.stack.pop();
  checkEndTrigger(s);
}

function ask(s: GameState, pending: Pending) {
  s.pending = pending;
  s.pendingId += 1;
}

// ── answering ───────────────────────────────────────────────────────────────

export class RuleError extends Error {}

/**
 * Apply a player's answer to the outstanding prompt.
 *
 * `pendingId` is not decoration. Every front end can double-fire — a double tap
 * on a phone, a React re-render, a resent frame after a reconnect — and without
 * it the second copy answers whatever prompt happens to be open by then, which
 * is how a "return one chip" tap silently becomes "trash Excalibur".
 */
export function answer(s: GameState, playerId: string, pendingId: number, ans: Answer): void {
  const pending = s.pending;
  if (!pending) throw new RuleError('Nothing to answer.');
  // The Fisher King offer is asked between resolutions, with nothing on the
  // stack. It still needs a frame to answer against. Done here rather than in a
  // wrapper so there is no second entry point that can skip it.
  if (pending.kind === 'confirm' && pending.chip === 'fisherKing') ensureStubForFlip(s);
  if (pendingId !== s.pendingId) throw new RuleError('That choice was already made.');
  if (pending.playerId !== playerId) throw new RuleError('That is not your choice to make.');
  if (pending.kind !== ans.kind) throw new RuleError('That answer does not fit the question.');

  const res = s.stack[s.stack.length - 1];
  if (!res) throw new RuleError('Nothing is resolving.');
  validate(s, pending, ans);

  s.pending = null;
  applyAnswer(s, res, pending, ans);
  pump(s);
}

function validate(s: GameState, pending: Pending, ans: Answer) {
  switch (pending.kind) {
    case 'region': {
      const a = ans as Extract<Answer, { kind: 'region' }>;
      if (!Number.isInteger(a.region) || a.region < 0 || a.region > 2) throw new RuleError('No such region.');
      if (!pending.allowEmpty && s.regions[a.region as 0 | 1 | 2].length === 0) throw new RuleError('That region is empty.');
      return;
    }
    case 'mode': {
      const a = ans as Extract<Answer, { kind: 'mode' }>;
      if (!pending.modes.some((m) => m.id === a.mode)) throw new RuleError('Not one of the options.');
      return;
    }
    case 'players': {
      const a = ans as Extract<Answer, { kind: 'players' }>;
      const uniq = [...new Set(a.players)];
      if (uniq.length !== a.players.length) throw new RuleError('You pointed at the same player twice.');
      if (uniq.length < pending.min || uniq.length > pending.max) throw new RuleError('Wrong number of players.');
      if (uniq.some((id) => !pending.candidates.includes(id))) throw new RuleError('You cannot point there.');
      return;
    }
    case 'hand': {
      const a = ans as Extract<Answer, { kind: 'hand' }>;
      if (a.chips.length < pending.min || a.chips.length > pending.max) throw new RuleError('Wrong number of chips.');
      // Multiset containment: picking two Merchants is legal only if two are eligible.
      const pool = pending.eligible.slice();
      for (const c of a.chips) if (!takeOne(pool, c)) throw new RuleError('You do not hold that.');
      return;
    }
    case 'direction': {
      const a = ans as Extract<Answer, { kind: 'direction' }>;
      if (a.direction !== 'left' && a.direction !== 'right') throw new RuleError('Left or right.');
      return;
    }
    case 'pick': {
      const a = ans as Extract<Answer, { kind: 'pick' }>;
      if (a.index === null) {
        if (!pending.optional) throw new RuleError('You must choose one.');
        return;
      }
      if (!Number.isInteger(a.index) || a.index < 0 || a.index >= pending.chips.length) throw new RuleError('No such chip.');
      return;
    }
    case 'confirm':
      return;
    case 'peek': {
      const a = ans as Extract<Answer, { kind: 'peek' }>;
      if (pending.mode === 'expose') {
        if (a.index === null || !Number.isInteger(a.index) || a.index < 0 || a.index >= pending.peek.length) {
          throw new RuleError('Choose a chip to show.');
        }
      }
      return;
    }
    case 'sword': {
      const a = ans as Extract<Answer, { kind: 'sword' }>;
      if (!pending.swords.includes(a.chip)) throw new RuleError('You do not hold that sword.');
      return;
    }
    case 'played': {
      const a = ans as Extract<Answer, { kind: 'played' }>;
      if (!pending.options.some((o) => o.ownerId === a.ownerId && o.chip === a.chip)) throw new RuleError('That chip is not in play.');
      return;
    }
  }
}

/** Record the answer into scratch, then let `runAbility` pick up at the next step. */
function applyAnswer(s: GameState, res: Resolution, pending: Pending, ans: Answer) {
  const sc = res.scratch;
  switch (ans.kind) {
    case 'region':
      sc.cursor = ans.region;
      break;
    case 'mode':
      sc.mode = ans.mode;
      break;
    case 'players':
      sc.targets = ans.players;
      break;
    case 'hand':
      sc.place = ans.chips;
      break;
    case 'direction':
      sc.direction = ans.direction;
      break;
    case 'pick':
      sc.cursor = ans.index === null ? -1 : ans.index;
      break;
    case 'confirm':
      sc.mode = ans.yes ? 'yes' : 'no';
      break;
    case 'peek':
      sc.cursor = ans.index ?? -1;
      sc.mode = ans.swap ? 'swap' : 'no';
      break;
    case 'sword':
      sc.place = [ans.chip];
      break;
    case 'played':
      sc.targets = [ans.ownerId];
      sc.place = [ans.chip];
      break;
  }
  // The Fisher King offer is not part of any ability's step machine; it is asked
  // between resolutions, against the stub `ensureStubForFlip` pushed for it. The
  // stub is identified by its empty actor — keying off the chip id alone would
  // also match a Fisher King that was genuinely played this round.
  if (pending.kind === 'confirm' && pending.chip === 'fisherKing' && res.actor === '') {
    resolveFisherKingFlip(s, pending.playerId, ans.kind === 'confirm' && ans.yes);
    return;
  }
  res.step += 1;
}

function resolveFisherKingFlip(s: GameState, playerId: string, yes: boolean) {
  const p = playerOf(s, playerId);
  if (!p || !yes) return;
  if (flip(s)) {
    p.fisherKingUp = true;
    log(s, `${p.name} flips the Fisher King faceup. Nothing can be stolen from them this round.`, true);
  } else {
    takeOne(p.permanents, 'fisherKing');
    trash(s, 'fisherKing');
    log(s, `${p.name} flips the Fisher King and it fails. It is trashed.`);
  }
}

/**
 * The Fisher King offer is asked while some OTHER ability sits on top of the
 * stack, so `answer` needs a resolution to hang the id off. `pump` only asks it
 * with an empty stack, so give it a stub to answer against.
 */
function ensureStubForFlip(s: GameState) {
  if (s.stack.length === 0) s.stack.push({ chip: 'fisherKing', actor: '', step: 0, scratch: {}, nested: true });
}

// ── abilities ───────────────────────────────────────────────────────────────

/**
 * One switch, one ability, stepping on `res.step`.
 *
 * Every branch must either `ask` (and return, leaving `step` for `applyAnswer`
 * to advance) or reach `done`. A branch that does neither is the bug the pump's
 * guard counter exists to surface.
 */
function runAbility(s: GameState, res: Resolution) {
  const actor = playerOf(s, res.actor);
  // The actor can be removed from the game only in modes that allow it; guard
  // anyway, because a resolution outliving its owner would spin the pump.
  if (!actor && res.chip !== 'fisherKing') {
    done(s);
    return;
  }
  const me = actor!;
  const sc = res.scratch;
  const others = () => s.players.filter((p) => p.id !== me.id);

  switch (res.chip) {
    // ── the swords never resolve; they cannot be played ──────────────────────
    case 'excalibur':
    case 'cursedBlade':
    case 'squireSword':
      done(s);
      return;

    case 'fisherKing': {
      // An empty actor is the stub `ensureStubForFlip` pushed to host the flip
      // offer; the flip has already been applied by then, so there is nothing
      // left to do but clear it off the stack.
      if (res.actor === '') {
        done(s);
        return;
      }
      // "On reveal, this chip stays by you as a faceup played chip." It enters
      // play NOW, not at end of round — which is what makes it eligible for its
      // own flip offer during this round's resolve step, the round it was played.
      me.permanents.push('fisherKing');
      // Only clear the fist if the Fisher King is what is IN it. Morgana can cast
      // one straight out of Avalon, and blanking `played` there would delete the
      // Morgana chip itself out of the game.
      if (me.played === 'fisherKing') me.played = null;
      log(s, `${me.name} sets the Fisher King before them.`);
      done(s);
      return;
    }

    // ── draw-and-give-back family ───────────────────────────────────────────
    case 'peasant':
    case 'merchant':
    case 'artisan': {
      const n = res.chip === 'peasant' ? 1 : 2;
      const placeBack = res.chip === 'artisan' ? 2 : 1;
      switch (res.step) {
        case 0:
          ask(s, {
            kind: 'mode',
            playerId: me.id,
            chip: res.chip,
            prompt: `Draw ${n} from Avalon, or steal ${n} from a player?`,
            modes: [
              { id: 'draw', label: `Draw ${n}` },
              { id: 'steal', label: `Steal ${n}` },
            ],
          });
          return;
        case 1:
          if (sc.mode === 'draw') {
            sc.left = n;
            sc.returnTo = 'avalon';
            res.step = 2;
            runAbility(s, res);
            return;
          }
          sc.returnTo = 'victim';
          {
            const cands = others().filter((p) => reachable(p).length > 0 && !p.fisherKingUp);
            if (cands.length === 0) {
              log(s, `${me.name} finds nobody worth robbing.`);
              // No victim means no "where it came from" to return to. Without
              // this the return step would take a chip out of the hand and hand
              // it to nobody, quietly destroying it.
              sc.returnTo = 'avalon';
              res.step = 4;
              runAbility(s, res);
              return;
            }
            ask(s, {
              kind: 'players',
              playerId: me.id,
              chip: res.chip,
              prompt: `Steal ${n} from whom?`,
              min: 1,
              max: 1,
              candidates: cands.map((p) => p.id),
            });
          }
          return;
        case 2: {
          // Draw loop.
          if (sc.mode === 'steal') {
            const victim = playerOf(s, sc.targets![0]!)!;
            sc.victimId = victim.id;
            const got = steal(s, me, victim, n);
            log(s, `${me.name} takes ${got.length} chip${got.length === 1 ? '' : 's'} from ${victim.name}.`);
            res.step = 4;
            runAbility(s, res);
            return;
          }
          if ((sc.left ?? 0) <= 0 || avalonCount(s) === 0) {
            res.step = 4;
            runAbility(s, res);
            return;
          }
          ask(s, {
            kind: 'region',
            playerId: me.id,
            chip: res.chip,
            prompt: `Draw from which region? (${sc.left} to go)`,
            allowEmpty: false,
          });
          return;
        }
        case 3: {
          const chip = drawFrom(s, me, sc.cursor ?? 0);
          if (chip) (sc.drawn ??= []).push(chip);
          sc.left = (sc.left ?? 1) - 1;
          res.step = 2;
          runAbility(s, res);
          return;
        }
        case 4: {
          // Return / place back.
          const eligible = me.hand.slice();
          if (eligible.length === 0) {
            done(s);
            return;
          }
          const count = Math.min(placeBack, eligible.length);
          ask(s, {
            kind: 'hand',
            playerId: me.id,
            chip: res.chip,
            prompt:
              res.chip === 'artisan'
                ? `Place ${count} chip${count === 1 ? '' : 's'} back into Avalon.`
                : sc.returnTo === 'victim'
                  ? `Return ${count} chip to ${nameOf(s, sc.victimId ?? '')}.`
                  : `Return ${count} chip to Avalon.`,
            min: count,
            max: count,
            eligible,
          });
          return;
        }
        case 5: {
          const chosen = sc.place ?? [];
          if (sc.returnTo === 'victim' && res.chip !== 'artisan') {
            const victim = playerOf(s, sc.victimId ?? '');
            for (const c of chosen) {
              if (takeOne(me.hand, c) && victim) victim.hand.push(c);
            }
            log(s, `${me.name} hands ${chosen.length} chip back to ${victim?.name ?? 'them'}.`);
            done(s);
            return;
          }
          // Back into Avalon, one region choice per chip.
          sc.left = chosen.length;
          res.step = 6;
          runAbility(s, res);
          return;
        }
        case 6: {
          const chosen = sc.place ?? [];
          const idx = chosen.length - (sc.left ?? 0);
          if ((sc.left ?? 0) <= 0) {
            log(s, `${me.name} slips ${chosen.length} chip${chosen.length === 1 ? '' : 's'} back into Avalon.`);
            done(s);
            return;
          }
          ask(s, {
            kind: 'region',
            playerId: me.id,
            chip: res.chip,
            prompt: `Put ${CHIPS[chosen[idx]!].name} into which region?`,
            allowEmpty: true,
          });
          return;
        }
        case 7: {
          const chosen = sc.place ?? [];
          const idx = chosen.length - (sc.left ?? 1);
          const chip = chosen[idx]!;
          if (takeOne(me.hand, chip)) placeIn(s, chip, sc.cursor ?? 0);
          sc.left = (sc.left ?? 1) - 1;
          res.step = 6;
          runAbility(s, res);
          return;
        }
      }
      done(s);
      return;
    }

    // ── baroness: draw 2, then burn one ─────────────────────────────────────
    case 'baroness': {
      switch (res.step) {
        case 0:
          sc.left = 2;
          res.step = 1;
          runAbility(s, res);
          return;
        case 1:
          if ((sc.left ?? 0) <= 0 || avalonCount(s) === 0) {
            res.step = 3;
            runAbility(s, res);
            return;
          }
          ask(s, {
            kind: 'region',
            playerId: me.id,
            chip: 'baroness',
            prompt: `Draw from which region? (${sc.left} to go)`,
            allowEmpty: false,
          });
          return;
        case 2:
          drawFrom(s, me, sc.cursor ?? 0);
          sc.left = (sc.left ?? 1) - 1;
          res.step = 1;
          runAbility(s, res);
          return;
        case 3: {
          const eligible = me.hand.filter(isTrashable);
          if (eligible.length === 0) {
            log(s, `${me.name} holds nothing but swords — nothing can be trashed.`);
            done(s);
            return;
          }
          ask(s, {
            kind: 'hand',
            playerId: me.id,
            chip: 'baroness',
            prompt: 'Trash one chip to the box. You cannot trash a sword.',
            min: 1,
            max: 1,
            eligible,
          });
          return;
        }
        case 4: {
          const chip = sc.place?.[0];
          if (chip && takeOne(me.hand, chip)) {
            trash(s, chip);
            log(s, `${me.name} trashes ${CHIPS[chip].name}.`, true);
          }
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    // ── priest: draw 2, and the table hears about a sword ────────────────────
    case 'priest': {
      switch (res.step) {
        case 0:
          sc.left = 2;
          res.step = 1;
          runAbility(s, res);
          return;
        case 1:
          if ((sc.left ?? 0) <= 0 || avalonCount(s) === 0) {
            const drew = sc.drawn ?? [];
            const swords = drew.filter(isSword);
            if (swords.length) {
              // The one piece of information this chip exists to leak.
              log(s, `${me.name} shouts "Huzzah!" — a sword came out of Avalon.`, true);
            } else {
              log(s, `${me.name} draws two, and says nothing.`);
            }
            done(s);
            return;
          }
          ask(s, {
            kind: 'region',
            playerId: me.id,
            chip: 'priest',
            prompt: `Draw from which region? (${sc.left} to go)`,
            allowEmpty: false,
          });
          return;
        case 2: {
          const chip = drawFrom(s, me, sc.cursor ?? 0);
          if (chip) (sc.drawn ??= []).push(chip);
          sc.left = (sc.left ?? 1) - 1;
          res.step = 1;
          runAbility(s, res);
          return;
        }
      }
      done(s);
      return;
    }

    // ── morgana: draw 2, fire one off immediately ───────────────────────────
    case 'morgana': {
      switch (res.step) {
        case 0:
          sc.left = 2;
          res.step = 1;
          runAbility(s, res);
          return;
        case 1:
          if ((sc.left ?? 0) <= 0 || avalonCount(s) === 0) {
            res.step = 3;
            runAbility(s, res);
            return;
          }
          ask(s, {
            kind: 'region',
            playerId: me.id,
            chip: 'morgana',
            prompt: `Draw from which region? (${sc.left} to go)`,
            allowEmpty: false,
          });
          return;
        case 2: {
          const chip = drawFrom(s, me, sc.cursor ?? 0);
          if (chip) (sc.drawn ??= []).push(chip);
          sc.left = (sc.left ?? 1) - 1;
          res.step = 1;
          runAbility(s, res);
          return;
        }
        case 3: {
          // A sword cannot be played, so it cannot be resolved here either.
          const options = (sc.drawn ?? []).filter((c) => !isSword(c));
          if (options.length === 0) {
            log(s, `${me.name} draws two, with nothing worth casting.`);
            done(s);
            return;
          }
          sc.revealed = options;
          ask(s, {
            kind: 'pick',
            playerId: me.id,
            chip: 'morgana',
            prompt: 'Reveal and resolve one of the chips you just drew?',
            chips: options,
            optional: true,
          });
          return;
        }
        case 4: {
          const idx = sc.cursor ?? -1;
          if (idx < 0) {
            log(s, `${me.name} keeps what she drew.`);
            done(s);
            return;
          }
          const chip = (sc.revealed ?? [])[idx];
          if (!chip || !takeOne(me.hand, chip)) {
            done(s);
            return;
          }
          log(s, `${me.name} reveals ${CHIPS[chip].name} and casts it at once.`, true);
          // "Trashing it as normal" — but a Permanent is never trashed; being set
          // faceup in front of you IS its resolution. Trashing one here and then
          // resolving it puts the same chip in the box and in play at once.
          if (!CHIPS[chip].permanent) trash(s, chip);
          res.step = 5;
          push(s, chip, me.id, true);
          return;
        }
        case 5:
          done(s);
          return;
      }
      done(s);
      return;
    }

    // ── thief / robyn: taking things ────────────────────────────────────────
    case 'thief': {
      const canRob = (exclude: string[]) =>
        others().filter((p) => !exclude.includes(p.id) && reachable(p).length > 0 && !p.fisherKingUp);
      switch (res.step) {
        case 0: {
          const cands = canRob([]);
          if (cands.length === 0) {
            log(s, `${me.name} finds every pocket empty.`);
            done(s);
            return;
          }
          ask(s, {
            kind: 'players',
            playerId: me.id,
            chip: 'thief',
            prompt: 'Steal 1 chip from whom?',
            min: 1,
            max: 1,
            candidates: cands.map((p) => p.id),
          });
          return;
        }
        case 1: {
          const first = sc.targets![0]!;
          steal(s, me, playerOf(s, first)!, 1);
          log(s, `${me.name} lifts a chip from ${nameOf(s, first)}.`);
          sc.givers = [first];
          const cands = canRob([first]);
          if (cands.length === 0) {
            done(s);
            return;
          }
          ask(s, {
            kind: 'players',
            playerId: me.id,
            chip: 'thief',
            prompt: 'And 1 more, from a different player.',
            min: 1,
            max: 1,
            candidates: cands.map((p) => p.id),
          });
          return;
        }
        case 2: {
          const second = sc.targets![0]!;
          steal(s, me, playerOf(s, second)!, 1);
          log(s, `${me.name} lifts another from ${nameOf(s, second)}.`);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'robyn': {
      switch (res.step) {
        case 0:
        case 2: {
          if (me.hand.length >= 7) {
            log(s, `${me.name} stops at ${me.hand.length} chips.`);
            done(s);
            return;
          }
          const cands = others().filter((p) => reachable(p).length > 0 && !p.fisherKingUp);
          if (cands.length === 0) {
            log(s, `${me.name} has robbed the table bare.`);
            done(s);
            return;
          }
          ask(s, {
            kind: 'players',
            playerId: me.id,
            chip: 'robyn',
            prompt: `Steal 1 chip — you must keep going until you hold 7. (${me.hand.length} now)`,
            min: 1,
            max: 1,
            candidates: cands.map((p) => p.id),
          });
          return;
        }
        case 1: {
          const target = sc.targets![0]!;
          const got = steal(s, me, playerOf(s, target)!, 1);
          if (got.length === 0) {
            // A Fisher King between her and the last chip she needs.
            done(s);
            return;
          }
          log(s, `${me.name} takes from ${nameOf(s, target)}.`);
          res.step = 2;
          runAbility(s, res);
          return;
        }
      }
      done(s);
      return;
    }

    // ── the sword movers ────────────────────────────────────────────────────
    case 'greenKnight': {
      const taken = harvestSwords(s, { exclude: me.id });
      for (const t of taken) me.hand.push(t.chip);
      log(
        s,
        taken.length
          ? `The Green Knight calls in every sword. ${me.name} takes ${taken.length}.`
          : 'The Green Knight calls, and no sword answers.',
        taken.length > 0,
      );
      done(s);
      return;
    }

    case 'merlin': {
      switch (res.step) {
        case 0: {
          const taken = harvestSwords(s, { exclude: me.id });
          for (const t of taken) me.hand.push(t.chip);
          sc.givers = [...new Set(taken.map((t) => t.ownerId))];
          // Turn order decides who steals back first, as it decides everything.
          sc.givers.sort((a, b) => s.order.indexOf(a) - s.order.indexOf(b));
          sc.cursor = 0;
          log(
            s,
            taken.length
              ? `Merlin gathers ${taken.length} sword${taken.length === 1 ? '' : 's'} to ${me.name}.`
              : 'Merlin gathers, and finds no sword to take.',
            taken.length > 0,
          );
          res.step = 1;
          runAbility(s, res);
          return;
        }
        case 1: {
          const givers = sc.givers ?? [];
          const i = sc.cursor ?? 0;
          if (i >= givers.length) {
            done(s);
            return;
          }
          const giver = playerOf(s, givers[i]!);
          sc.cursor = i + 1;
          if (giver) {
            const got = steal(s, giver, me, 1);
            if (got.length) log(s, `${giver.name} takes a chip back from ${me.name}.`);
          }
          res.step = 1;
          runAbility(s, res);
          return;
        }
      }
      done(s);
      return;
    }

    case 'gawain': {
      switch (res.step) {
        case 0: {
          const cands = others();
          if (cands.length === 0) {
            done(s);
            return;
          }
          ask(s, {
            kind: 'players',
            playerId: me.id,
            chip: 'gawain',
            prompt: 'Demand a sword from whom?',
            min: 1,
            max: 1,
            candidates: cands.map((p) => p.id),
          });
          return;
        }
        case 1: {
          const victim = playerOf(s, sc.targets![0]!)!;
          const swords = swordsOf(victim);
          if (swords.length === 0) {
            log(s, `${me.name} demands a sword of ${victim.name}, who has none.`);
            done(s);
            return;
          }
          if (swords.length === 1) {
            takeOne(victim.hand, swords[0]!);
            me.hand.push(swords[0]!);
            log(s, `${victim.name} surrenders a sword to ${me.name}.`, true);
            done(s);
            return;
          }
          sc.victimId = victim.id;
          // Their choice, not Gawain's.
          ask(s, {
            kind: 'sword',
            playerId: victim.id,
            chip: 'gawain',
            prompt: `${me.name} demands a sword. Which do you give up?`,
            swords,
          });
          return;
        }
        case 2: {
          const victim = playerOf(s, sc.victimId ?? '')!;
          const chip = sc.place?.[0];
          if (chip && victim && takeOne(victim.hand, chip)) {
            me.hand.push(chip);
            log(s, `${victim.name} surrenders a sword to ${me.name}.`, true);
          }
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'arthur': {
      // Everyone holds their swords out facedown; one is taken blind. Including
      // the actor's own — they cannot tell theirs apart either.
      const taken = harvestSwords(s);
      if (taken.length === 0) {
        log(s, 'Arthur calls for the swords. None are held.');
        done(s);
        return;
      }
      const pick = randInt(s, taken.length);
      taken.forEach((t, i) => {
        if (i === pick) me.hand.push(t.chip);
        else playerOf(s, t.ownerId)?.hand.push(t.chip);
      });
      log(s, `Arthur takes one sword blind from ${taken.length} held out. ${me.name} keeps it.`, true);
      done(s);
      return;
    }

    case 'guinevere': {
      switch (res.step) {
        case 0: {
          // The region is chosen BEFORE the swords are gathered, so no chip is
          // ever in limbo across a prompt. Ordering it the other way leaves the
          // harvested swords living in `scratch` and nowhere else — they exist
          // in no hand, no region and no trash until the answer arrives, and the
          // conservation invariant is right to call that a leak.
          //
          // It costs nothing in fidelity: she is choosing blind either way.
          const held = s.players.reduce((n, p) => n + swordsOf(p).length, 0);
          if (held === 0) {
            log(s, 'Guinevere calls for the swords. None are held.');
            done(s);
            return;
          }
          ask(s, {
            kind: 'region',
            playerId: me.id,
            chip: 'guinevere',
            prompt: `Bury ${Math.min(2, held)} sword${held === 1 ? '' : 's'} in which region? You cannot see which.`,
            allowEmpty: true,
          });
          return;
        }
        case 1: {
          const taken = harvestSwords(s);
          shuffleInPlace(s, taken);
          const bury = taken.slice(0, 2);
          for (const t of bury) placeIn(s, t.chip, sc.cursor ?? 0);
          for (const t of taken.slice(2)) playerOf(s, t.ownerId)?.hand.push(t.chip);
          log(s, `Guinevere buries ${bury.length} sword${bury.length === 1 ? '' : 's'} back in Avalon.`, true);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'uther': {
      switch (res.step) {
        case 0:
          ask(s, { kind: 'direction', playerId: me.id, chip: 'uther', prompt: 'Pass every sword which way?' });
          return;
        case 1: {
          const dir = sc.direction ?? 'left';
          // Snapshot first: passing in sequence would carry one sword all the
          // way round the table instead of moving each along by one seat.
          const moving = s.players.map((p) => ({ p, swords: swordsOf(p) }));
          for (const { p, swords } of moving) for (const sw of swords) takeOne(p.hand, sw);
          let moved = 0;
          for (const { p, swords } of moving) {
            const to = neighbour(s, p.id, dir);
            for (const sw of swords) {
              to.hand.push(sw);
              moved++;
            }
          }
          log(s, moved ? `Uther sends ${moved} sword${moved === 1 ? '' : 's'} to the ${dir}.` : 'Uther commands, and no sword moves.', moved > 0);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    // ── hand movers ─────────────────────────────────────────────────────────
    case 'ladyOfTheLake': {
      switch (res.step) {
        case 0:
          ask(s, { kind: 'direction', playerId: me.id, chip: 'ladyOfTheLake', prompt: 'Pass every hand which way?' });
          return;
        case 1: {
          const dir = sc.direction ?? 'left';
          // A protected chip stays with its owner: it is closed in a fist, not
          // in the hand being passed.
          const held = s.players.map((p) => {
            const keep: ChipId[] = [];
            if (p.protectedChip && takeOne(p.hand, p.protectedChip)) keep.push(p.protectedChip);
            return { p, moving: p.hand.slice(), keep };
          });
          for (const h of held) h.p.hand = [];
          for (const h of held) {
            const to = neighbour(s, h.p.id, dir);
            to.hand.push(...h.moving);
          }
          for (const h of held) h.p.hand.push(...h.keep);
          log(s, `The Lady of the Lake turns every hand to the ${dir}.`, true);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'spirit': {
      switch (res.step) {
        case 0:
          ask(s, { kind: 'direction', playerId: me.id, chip: 'spirit', prompt: 'Everyone steals from which side?' });
          return;
        case 1: {
          const dir = sc.direction ?? 'left';
          // Simultaneous: resolve against a snapshot, so a chip stolen into a
          // hand cannot be stolen out of it again in the same sweep.
          const picks: { thief: Player; chip: ChipId; from: Player }[] = [];
          for (const p of s.players) {
            const from = neighbour(s, p.id, dir);
            if (from.id === p.id || from.fisherKingUp) continue;
            const pool = reachable(from);
            if (pool.length === 0) continue;
            picks.push({ thief: p, chip: pool[randInt(s, pool.length)]!, from });
          }
          for (const pick of picks) {
            if (takeOne(pick.from.hand, pick.chip)) pick.thief.hand.push(pick.chip);
          }
          log(s, `Spirits move. Everyone takes a chip from the ${dir}.`, true);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'mordred': {
      switch (res.step) {
        case 0: {
          const cands = others();
          if (cands.length === 0) {
            done(s);
            return;
          }
          ask(s, {
            kind: 'players',
            playerId: me.id,
            chip: 'mordred',
            prompt: 'Peek at whose hand?',
            min: 1,
            max: 1,
            candidates: cands.map((p) => p.id),
          });
          return;
        }
        case 1: {
          const target = playerOf(s, sc.targets![0]!)!;
          sc.victimId = target.id;
          log(s, `${me.name} looks into ${target.name}'s hand.`);
          ask(s, {
            kind: 'peek',
            playerId: me.id,
            chip: 'mordred',
            prompt: `${target.name}'s hand. Take it?`,
            targetId: target.id,
            peek: target.hand.slice(),
            mode: 'swap',
          });
          return;
        }
        case 2: {
          if (sc.mode !== 'swap') {
            log(s, `${me.name} leaves ${nameOf(s, sc.victimId ?? '')}'s hand alone.`);
            done(s);
            return;
          }
          const target = playerOf(s, sc.victimId ?? '')!;
          const keepMine: ChipId[] = [];
          const keepTheirs: ChipId[] = [];
          if (me.protectedChip && takeOne(me.hand, me.protectedChip)) keepMine.push(me.protectedChip);
          if (target.protectedChip && takeOne(target.hand, target.protectedChip)) keepTheirs.push(target.protectedChip);
          const mine = me.hand;
          me.hand = target.hand;
          target.hand = mine;
          me.hand.push(...keepMine);
          target.hand.push(...keepTheirs);
          log(s, `${me.name} swaps hands with ${target.name}.`, true);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'spy': {
      switch (res.step) {
        case 0: {
          const cands = others().filter((p) => p.hand.length > 0);
          if (cands.length === 0) {
            done(s);
            return;
          }
          ask(s, {
            kind: 'players',
            playerId: me.id,
            chip: 'spy',
            prompt: 'Peek at whose hand?',
            min: 1,
            max: 1,
            candidates: cands.map((p) => p.id),
          });
          return;
        }
        case 1: {
          const target = playerOf(s, sc.targets![0]!)!;
          sc.victimId = target.id;
          ask(s, {
            kind: 'peek',
            playerId: me.id,
            chip: 'spy',
            prompt: `${target.name}'s hand. Show one chip to the table.`,
            targetId: target.id,
            peek: target.hand.slice(),
            mode: 'expose',
          });
          return;
        }
        case 2: {
          const target = playerOf(s, sc.victimId ?? '')!;
          const chip = target.hand[sc.cursor ?? 0];
          if (chip) log(s, `${me.name} exposes ${CHIPS[chip].name} in ${target.name}'s hand.`, true);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    // ── the rest ────────────────────────────────────────────────────────────
    case 'caelia': {
      switch (res.step) {
        case 0: {
          const cands = s.players.map((p) => p.id);
          ask(s, {
            kind: 'players',
            playerId: me.id,
            chip: 'caelia',
            prompt: 'Point at up to two players. They trash every chip of male artwork they hold.',
            min: 0,
            max: Math.min(2, cands.length),
            candidates: cands,
          });
          return;
        }
        case 1: {
          let burned = 0;
          for (const id of sc.targets ?? []) {
            const p = playerOf(s, id);
            if (!p) continue;
            // Never a sword: they are un-trashable, and silver-bordered besides.
            for (const chip of reachable(p)) {
              if (CHIPS[chip].gender === 'male' && isTrashable(chip)) {
                takeOne(p.hand, chip);
                trash(s, chip);
                burned++;
              }
            }
          }
          log(s, burned ? `Caelia's briar takes ${burned} chip${burned === 1 ? '' : 's'} to the box.` : 'Caelia points, and nothing burns.', burned > 0);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'percival': {
      switch (res.step) {
        case 0: {
          // Four chips turned faceup for the whole table — public information,
          // and the reason Percival is as much a gift to the table as to you.
          const pool: { chip: ChipId; region: number }[] = [];
          for (let r = 0; r < 3; r++) for (const c of s.regions[r as 0 | 1 | 2]) pool.push({ chip: c, region: r });
          if (pool.length === 0) {
            log(s, 'Percival searches an empty Avalon.');
            done(s);
            return;
          }
          shuffleInPlace(s, pool);
          const four = pool.slice(0, 4);
          sc.revealed = four.map((f) => f.chip);
          sc.targets = four.map((f) => String(f.region));
          log(s, `Percival turns up ${sc.revealed.map((c) => CHIPS[c].name).join(', ')}.`, true);
          ask(s, {
            kind: 'pick',
            playerId: me.id,
            chip: 'percival',
            prompt: 'Take one. The rest go back facedown.',
            chips: sc.revealed,
            optional: false,
          });
          return;
        }
        case 1: {
          const idx = sc.cursor ?? 0;
          const chip = (sc.revealed ?? [])[idx];
          const region = Number((sc.targets ?? [])[idx] ?? 0);
          if (chip && takeOne(s.regions[region as 0 | 1 | 2], chip)) {
            me.hand.push(chip);
            log(s, `${me.name} takes ${CHIPS[chip].name}.`, true);
          }
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'galahad': {
      const eligible = me.hand.slice();
      if (res.step === 0) {
        if (eligible.length === 0) {
          done(s);
          return;
        }
        ask(s, {
          kind: 'hand',
          playerId: me.id,
          chip: 'galahad',
          prompt: 'Close your fist on one chip. Nothing can touch it this round.',
          min: 1,
          max: 1,
          eligible,
        });
        return;
      }
      const chip = sc.place?.[0];
      if (chip) {
        me.protectedChip = chip;
        log(s, `${me.name} closes a fist around one chip.`);
      }
      done(s);
      return;
    }

    case 'blackKnight': {
      switch (res.step) {
        case 0: {
          const a = flip(s);
          const b = flip(s);
          if (!a && !b) {
            log(s, `The Black Knight swings twice and misses.`);
            done(s);
            return;
          }
          const options: { ownerId: string; chip: ChipId }[] = [];
          for (const p of s.players) {
            if (p.id === me.id) continue;
            if (p.played && !p.playedKilled) options.push({ ownerId: p.id, chip: p.played });
            for (const perm of p.permanents) options.push({ ownerId: p.id, chip: perm });
          }
          if (options.length === 0) {
            log(s, 'The Black Knight lands a blow with nothing to take.');
            done(s);
            return;
          }
          log(s, `The Black Knight lands a blow.`, true);
          ask(s, {
            kind: 'played',
            playerId: me.id,
            chip: 'blackKnight',
            prompt: 'Take a played chip into your hand. Its owner loses its ability.',
            options,
          });
          return;
        }
        case 1: {
          const ownerId = (sc.targets ?? [])[0]!;
          const chip = sc.place?.[0];
          const owner = playerOf(s, ownerId);
          if (!owner || !chip) {
            done(s);
            return;
          }
          if (owner.played === chip && !owner.playedKilled) {
            owner.played = null;
            // A chip taken out of the fist never resolves.
            s.resolveQueue = s.resolveQueue.filter((id) => id !== ownerId);
            s.revealQueue = s.revealQueue.filter((id) => id !== ownerId);
          } else {
            takeOne(owner.permanents, chip);
            if (chip === 'fisherKing') owner.fisherKingUp = false;
          }
          me.hand.push(chip);
          log(s, `${me.name} tears ${CHIPS[chip].name} from ${owner.name}.`, true);
          done(s);
          return;
        }
      }
      done(s);
      return;
    }

    case 'lancelot': {
      // Every played chip flips, Lancelot's own included.
      const fallen: string[] = [];
      for (const p of s.players) {
        if (p.played && !p.playedKilled) {
          if (!flip(s)) {
            p.playedKilled = true;
            fallen.push(`${p.name}'s ${CHIPS[p.played].name}`);
            s.resolveQueue = s.resolveQueue.filter((id) => id !== p.id);
            s.revealQueue = s.revealQueue.filter((id) => id !== p.id);
          }
        }
        for (const perm of p.permanents.slice()) {
          if (!flip(s)) {
            takeOne(p.permanents, perm);
            trash(s, perm);
            if (perm === 'fisherKing') p.fisherKingUp = false;
            fallen.push(`${p.name}'s ${CHIPS[perm].name}`);
          }
        }
      }
      log(s, fallen.length ? `Lancelot rides through. ${fallen.join(', ')} fall.` : 'Lancelot rides through, and every chip holds.', true);
      done(s);
      return;
    }
  }

  done(s);
}

// ── player actions ──────────────────────────────────────────────────────────

export function drawChip(s: GameState, playerId: string, region: number) {
  if (s.phase !== 'draw') throw new RuleError('It is not the draw step.');
  const p = playerOf(s, playerId);
  if (!p) throw new RuleError('You are not in this game.');
  if (drawsOwed(p) <= 0) throw new RuleError('You already hold four.');
  if (!Number.isInteger(region) || region < 0 || region > 2) throw new RuleError('No such region.');
  if (s.regions[region as 0 | 1 | 2].length === 0) throw new RuleError('That region is empty.');
  drawFrom(s, p, region);
  checkEndTrigger(s);
  advanceIfDrawComplete(s);
}

export function playChip(s: GameState, playerId: string, chip: ChipId) {
  if (s.phase !== 'play') throw new RuleError('It is not the play step.');
  const p = playerOf(s, playerId);
  if (!p) throw new RuleError('You are not in this game.');
  if (p.played) throw new RuleError('Your fist is already closed.');
  if (isSword(chip)) throw new RuleError('A sword cannot be played.');
  if (!takeOne(p.hand, chip)) throw new RuleError('You do not hold that.');
  p.played = chip;
  advanceIfPlayComplete(s);
}

/** Undo a play while fists are still closed — nobody has seen it. */
export function unplayChip(s: GameState, playerId: string) {
  if (s.phase !== 'play') throw new RuleError('Too late.');
  const p = playerOf(s, playerId);
  if (!p || !p.played) throw new RuleError('Nothing to take back.');
  p.hand.push(p.played);
  p.played = null;
}

/** Alias kept so call sites read symmetrically with `drawChip` / `playChip`. */
export const answerPending = answer;

/**
 * Everything a player can do, as one value.
 *
 * This is what lets the bot table, the pass-and-play table and the networked
 * table render from the same component: each of them produces a `GameAction`,
 * and the only difference is whether it goes straight into `applyAction` or out
 * through a socket to a server that calls `applyAction` for them.
 */
export type GameAction =
  | { t: 'draw'; region: number }
  | { t: 'play'; chip: ChipId }
  | { t: 'unplay' }
  | { t: 'answer'; pendingId: number; answer: Answer };

export function applyAction(s: GameState, playerId: string, action: GameAction): void {
  switch (action.t) {
    case 'draw':
      drawChip(s, playerId, action.region);
      return;
    case 'play':
      playChip(s, playerId, action.chip);
      return;
    case 'unplay':
      unplayChip(s, playerId);
      return;
    case 'answer':
      answer(s, playerId, action.pendingId, action.answer);
      return;
  }
}

/**
 * Who the game is waiting on right now.
 *
 * Pass-and-play needs this to know whose turn it is to be handed the phone, and
 * the networked table uses it to say "waiting for Ada" instead of showing a
 * spinner with no subject.
 */
export function waitingOn(s: GameState): string[] {
  if (s.phase === 'over') return [];
  if (s.pending) return [s.pending.playerId];
  if (s.phase === 'draw') return s.players.filter((p) => drawsOwed(p) > 0).map((p) => p.id);
  if (s.phase === 'play') {
    return s.players.filter((p) => !p.played && p.hand.some((c) => !isSword(c))).map((p) => p.id);
  }
  return [];
}

// ── redaction ───────────────────────────────────────────────────────────────

/** What one player is allowed to see. Built per recipient; never shared. */
export interface PlayerView {
  id: string;
  name: string;
  /** Your own chips. `null` for everyone else — the bytes never leave the server. */
  hand: ChipId[] | null;
  handCount: number;
  /** Whether a fist is closed. What is in it stays hidden until reveal. */
  hasPlayed: boolean;
  /** Populated from reveal onwards. */
  played: ChipId | null;
  playedKilled: boolean;
  permanents: ChipId[];
  fisherKingUp: boolean;
  protectedCount: number;
  isBot: boolean;
  isCrown: boolean;
  swordCount: number | null;
}

export interface View {
  phase: Phase;
  round: number;
  finalRound: boolean;
  you: {
    id: string;
    hand: ChipId[];
    played: ChipId | null;
    drawsOwed: number;
    /** Your prompt, or null. Another player's prompt is never sent to you. */
    pending: Pending | null;
    pendingId: number;
    protectedChip: ChipId | null;
  };
  players: PlayerView[];
  /** Counts only. Which chips sit in a region is the game's core secret. */
  regions: [number, number, number];
  trash: ChipId[];
  log: LogEntry[];
  /** Whose prompt the table is waiting on, so everyone else sees a reason. */
  waitingOn: { playerId: string; prompt: string } | null;
  /**
   * Whose chip is on the table right now, during the two ordered steps.
   *
   * Separate from `waitingOn`, which only exists while somebody has been asked a
   * question. A chip can be mid-resolution without asking anything, and the table
   * still needs to know whose it is — otherwise the only honest thing the UI can
   * say is "waiting", with no subject.
   *
   * Leaks nothing: turn order is already public in `order`, and every played chip
   * is public from reveal onwards.
   */
  active: { playerId: string; step: 'reveal' | 'resolve'; index: number; total: number } | null;
  outcome: Outcome | null;
  order: string[];
}

/**
 * The one rule this file exists to enforce, alongside the rules of the game: a
 * field you may not see is absent from the object, not flagged on it. There is
 * no client-side filtering anywhere in this app.
 *
 * The two things worth guarding hardest are other players' hands and the
 * contents of Avalon's regions. Both are reduced to counts here, and the peek
 * payload inside a `Pending` is delivered only to the player who earned it.
 */
export function viewFor(s: GameState, playerId: string): View {
  const me = playerOf(s, playerId);
  const revealed = s.phase === 'reveal' || s.phase === 'resolve' || s.phase === 'over';
  const finished = s.phase === 'over';

  const players: PlayerView[] = s.players.map((p, i) => ({
    id: p.id,
    name: p.name,
    hand: p.id === playerId ? p.hand.slice() : finished ? p.hand.slice() : null,
    handCount: p.hand.length,
    hasPlayed: p.played !== null,
    played: revealed ? p.played : null,
    playedKilled: p.playedKilled,
    permanents: p.permanents.slice(),
    fisherKingUp: p.fisherKingUp,
    protectedCount: p.protectedChip ? 1 : 0,
    isBot: p.isBot,
    isCrown: i === s.crown,
    // Sword counts stay hidden. Several abilities call every sword out into the
    // open, and that public moment is the game's information economy — leaking
    // it continuously would flatten the whole thing.
    swordCount: finished ? p.hand.filter(isSword).length : null,
  }));

  return {
    phase: s.phase,
    round: s.round,
    finalRound: s.finalRound,
    you: {
      id: playerId,
      hand: me ? me.hand.slice() : [],
      played: me?.played ?? null,
      drawsOwed: me ? drawsOwed(me) : 0,
      pending: s.pending && s.pending.playerId === playerId ? s.pending : null,
      pendingId: s.pendingId,
      protectedChip: me?.protectedChip ?? null,
    },
    players,
    regions: [s.regions[0].length, s.regions[1].length, s.regions[2].length],
    trash: s.trash.slice(),
    log: s.log.slice(-40),
    waitingOn:
      s.pending && s.pending.playerId !== playerId
        ? { playerId: s.pending.playerId, prompt: promptSummary(s.pending) }
        : null,
    active: activeSeat(s),
    outcome: s.outcome,
    order: s.order.slice(),
  };
}

/**
 * Who is acting, and how far through the step they are.
 *
 * The bottom of the stack rather than the top: Morgana can push a second
 * resolution on top of her own, and the player the table is waiting on is still
 * Morgana's owner. Falls back to the pending's owner for the between-abilities
 * moment, and to the head of the queue when neither is set.
 */
function activeSeat(s: GameState): View['active'] {
  const step = s.phase === 'reveal' ? 'reveal' : s.phase === 'resolve' ? 'resolve' : null;
  if (!step) return null;
  const queue = step === 'reveal' ? s.revealQueue : s.resolveQueue;
  const total = step === 'reveal' ? s.revealTotal : s.resolveTotal;
  // The stub the Fisher King offer hangs off carries no actor; skip past it.
  const fromStack = s.stack.find((r) => r.actor)?.actor ?? null;
  const playerId = fromStack ?? s.pending?.playerId ?? queue[0] ?? null;
  if (!playerId) return null;
  // `total - queue.length` is how many have been taken off, which makes the one
  // in hand the Nth. Clamped because the Black Knight can pull a player out of
  // the queue after it was measured.
  const index = Math.max(1, Math.min(total, total - queue.length));
  return { playerId, step, index, total: Math.max(total, index) };
}

/** A version of the prompt safe to show the rest of the table. */
function promptSummary(p: Pending): string {
  switch (p.kind) {
    case 'peek':
      return `${CHIPS[p.chip].name} — looking at a hand`;
    case 'sword':
      return `${CHIPS[p.chip].name} — choosing a sword to give up`;
    default:
      return CHIPS[p.chip].name;
  }
}
